import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";
import { MessageSquare, Trash2, Search, X, Bookmark, MoreVertical, Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface ConversationOwner {
  full_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  last_message_sent?: string | null;
  user_id?: string;
  owner?: ConversationOwner | null;
  profile_name?: string | null;
  bookmarked?: boolean;
}

type TabType = "all" | "mine" | "bookmarked";

const TABS: { key: TabType; label: string }[] = [
  { key: "all", label: "All Chats" },
  { key: "mine", label: "My Chats" },
  { key: "bookmarked", label: "Saved" },
];

const MobileResearchList = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchConversations = async () => {
    if (!user) return;
    setLoading(true);

    if (activeTab === "mine") {
      const { data } = await supabase
        .from("conversations")
        .select("id, title, created_at, user_id, last_message_sent, profile_name, bookmarked")
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });
      setConversations(
        (data ?? []).map((c) => ({
          ...c,
          owner: null,
          profile_name: (c as any).profile_name ?? null,
          bookmarked: !!(c as any).bookmarked,
        })),
      );
    } else if (activeTab === "bookmarked") {
      const { data } = await supabase
        .from("conversations")
        .select(
          "id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)",
        )
        .eq("bookmarked", true)
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });
      setConversations(
        (data ?? []).map((c: any) => ({
          id: c.id,
          title: c.title,
          created_at: c.created_at,
          last_message_sent: c.last_message_sent,
          user_id: c.user_id,
          owner: c.profiles ?? null,
          profile_name: c.profile_name ?? null,
          bookmarked: true,
        })),
      );
    } else {
      let clientQuery = supabase
        .from("conversations")
        .select(
          "id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)",
        )
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });

      if (profile?.client_id) {
        clientQuery = clientQuery.eq("client_id", profile.client_id);
      }

      const [clientRes, ownRes] = await Promise.all([
        clientQuery,
        supabase
          .from("conversations")
          .select(
            "id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)",
          )
          .eq("user_id", user.id)
          .is("deleted_at", null)
          .order("last_message_sent", { ascending: false }),
      ]);

      const seen = new Set<string>();
      const merged: any[] = [];
      for (const c of [...(clientRes.data ?? []), ...(ownRes.data ?? [])]) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }

      setConversations(
        merged.map((c: any) => ({
          id: c.id,
          title: c.title,
          created_at: c.created_at,
          last_message_sent: c.last_message_sent,
          user_id: c.user_id,
          owner: c.profiles ?? null,
          profile_name: c.profile_name ?? null,
          bookmarked: !!c.bookmarked,
        })),
      );
    }

    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchConversations();
  }, [user, activeTab]);

  const deleteConversation = async (id: string) => {
    // Soft-delete: mark conversation (and related dashboards) as deleted
    const now = new Date().toISOString();
    await supabase.from("dashboard_snapshots").update({ deleted_at: now, deleted_by: user?.id } as any).eq("conversation_id", id).is("deleted_at", null);
    await supabase.from("conversations").update({ deleted_at: now, deleted_by: user?.id } as any).eq("id", id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setDeleteTarget(null);
  };

  const toggleBookmark = async (conv: Conversation) => {
    const newVal = !conv.bookmarked;
    await supabase.from("conversations").update({ bookmarked: newVal }).eq("id", conv.id);
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, bookmarked: newVal } : c))
    );
  };

  const shareConversation = async (conv: Conversation) => {
    const url = `${window.location.origin}/research?conv=${conv.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: conv.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  const isOwnConversation = (conv: Conversation) => conv.user_id === user?.id;

  const formatTime = (conv: Conversation) => {
    const d = new Date(conv.last_message_sent || conv.created_at);
    if (isToday(d)) return format(d, "h:mm a");
    if (isYesterday(d)) return `Yesterday`;
    return format(d, "MMM d");
  };

  const filtered = conversations.filter((c) => {
    const q = searchQuery.toLowerCase();
    return (
      c.title.toLowerCase().includes(q) ||
      (c.profile_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Tabs */}
      <div className="flex border-b border-border bg-card shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "flex-1 py-3 text-sm font-medium transition-colors border-b-2",
              activeTab === tab.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground",
            )}
          >
            {tab.key === "bookmarked" ? (
              <span className="flex items-center justify-center gap-1">
                <Bookmark
                  className={cn(
                    "h-3.5 w-3.5",
                    activeTab === "bookmarked" && "fill-primary text-primary",
                  )}
                />
                {tab.label}
              </span>
            ) : (
              tab.label
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-4 py-2 shrink-0 bg-card border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations…"
            className="pl-9 pr-9 h-9 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
            <MessageSquare className="h-8 w-8 opacity-30" />
            <span className="text-sm">No conversations found.</span>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/research?conv=${c.id}`)}
                className="flex items-start gap-3 px-4 py-3.5 active:bg-muted/50 cursor-pointer"
              >
                {/* Icon */}
                <div className="shrink-0 mt-0.5">
                  {c.bookmarked ? (
                    <Bookmark className="h-5 w-5 fill-primary text-primary" />
                  ) : (
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-snug line-clamp-2">
                    {c.title}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {activeTab === "all" && c.owner && (
                      <>
                        <Avatar className="h-4 w-4 shrink-0">
                          <AvatarImage src={c.owner.avatar_url ?? undefined} />
                          <AvatarFallback className="text-[7px] bg-muted">
                            {(c.owner.full_name || "?").charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                          {c.owner.full_name || "Unknown"}
                        </span>
                        <span className="text-xs text-muted-foreground">·</span>
                      </>
                    )}
                    <span className="text-xs text-muted-foreground">{formatTime(c)}</span>
                  </div>
                </div>

                {/* Kebab menu */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted active:bg-muted/80 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBookmark(c);
                        }}
                      >
                        <Bookmark className={cn("h-4 w-4 mr-2", c.bookmarked && "fill-primary text-primary")} />
                        {c.bookmarked ? "Remove Bookmark" : "Bookmark"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          shareConversation(c);
                        }}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      {isOwnConversation(c) && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(c.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its messages. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteConversation(deleteTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MobileResearchList;
